import PropTypes from "prop-types";
import React, { memo } from "react";
import { useIntl } from "react-intl";
import { Badge } from "../../../components/Badge";
import { Table } from "../../../components/Table";
import SecurityPostureSecureScore from "../../SecurityPosture/SecureScore/SecureScore";
import {
  formatComplianceAuditTemplates,
  getComplianceAuditStatusBadgeVariant,
} from "../utils";
import "./ComplianceAuditTable.scss";
import { getPostureRationale, getPostureStatusDetails } from "../../SecurityPosture/utils";

const propTypes = {
  /** Rows pre-formatted for PDF/export (see complianceAuditExport.js). */
  tableData: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  /** Hides filters/sort and renders a read-only table for PDF reports. */
  print: PropTypes.bool.isRequired,
};

function ComplianceAuditTable({
  tableData,
  print,
}) {
  const intl = useIntl();


  const formatNameColumn = (cell) => (
    <span className="Uic-ComplianceAuditTable-securityCheckCell">
      {cell}
    </span>
  );

  const formatStatusColumn = (cell, row) =>
    cell ? (
      <div className="Uic-ComplianceAuditTable-statusColumn">
        <div>
          <Badge
            variant={getComplianceAuditStatusBadgeVariant(cell, intl)}
            className="Uic-SecurityPostureTable-complianceBadge"
          >
            {cell}
          </Badge>
        </div>
        <div className="Uic-ComplianceAuditTable-statusDetailsCell">
          {getPostureStatusDetails(row, intl)}
        </div>
      </div>
    ) : null;

  const formatTemplatesColumn = (cell, row) =>
    formatComplianceAuditTemplates(row.formattedTemplates);

  const formatSecureScoreColumn = (cell, row) => {
    const { msSecureScore } = row;

    if (msSecureScore?.currentScore == null) {
      return null;
    }

    if (msSecureScore.maxScore != null) {
      return <SecurityPostureSecureScore {...msSecureScore} />;
    }

    return <span>{msSecureScore.currentScore}</span>;
  };

  const columns = [
    {
      dataField: "formattedName",
      text: intl.formatMessage({ id: "COMPLIANCE_AUDIT.SECURITY_CHECK" }),
      formatter: formatNameColumn,
      sort: !print,
      headerStyle: () => ({ width: "24%" }),
    },
    {
      dataField: "formattedStatus",
      text: intl.formatMessage({ id: "COMPLIANCE_AUDIT.STATUS" }),
      formatter: formatStatusColumn,
      sort: !print,
      headerStyle: () => ({ width: "20%" }),
    },
    {
      dataField: "description",
      text: intl.formatMessage({ id: "COMPLIANCE_AUDIT.DESCRIPTION" }),
      formatter: (_, row) => getPostureRationale(row, intl),
      headerStyle: () => ({ width: "28%" }),
    },
    {
      dataField: "category",
      text: intl.formatMessage({ id: "COMPLIANCE_AUDIT.CATEGORIES" }),
      sort: !print,
      headerStyle: () => ({ width: "10%" }),
    },
    {
      dataField: "formattedTemplates",
      text: intl.formatMessage({ id: "COMPLIANCE_AUDIT.TEMPLATES" }),
      formatter: formatTemplatesColumn,
      headerStyle: () => ({ width: "10%" }),
    },
    {
      dataField: "msSecureScore.currentScore",
      text: intl.formatMessage({ id: "COMPLIANCE_AUDIT.SECURE_SCORE" }),
      formatter: formatSecureScoreColumn,
      sort: !print,
      headerStyle: () => ({ width: "8%" }),
    },
  ];

  return (
    <div className="Uic-ComplianceAuditTable-tableWrapper">
      <Table
        data={tableData}
        keyField="checkId"
        columns={columns}
        hideHeader
        hidePagination
        readOnly={print}
      />
    </div>
  );
}

ComplianceAuditTable.propTypes = propTypes;

export default memo(ComplianceAuditTable);
